import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Grid,
  Paper,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchStatesAsync,
  selectAllStates,
  selectStatesLoading,
  selectStatesError,
} from "../../../../Slices/statesSlice";
import {
  isAuthenticated,
  updateStudentInfo,
} from "../../../../ApiService/ApiService";
import {
  isValidPin,
  isValidPhoneNumber,
  isValidAadhar,
} from "../../../../Utils/validFormDetails";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import CustomDialog from "../../../../Components/CustomDialog";

function UpdateInfoRedirect({ studentId, setShowRegisterStudent }) {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(fetchStatesAsync());
  }, [dispatch]);
  const states = useSelector(selectAllStates);
  const statesLoading = useSelector(selectStatesLoading);
  const statesError = useSelector(selectStatesError);
  const [dialog, setDialog] = useState({
    visible: false,
    title: "",
    message: "",
    buttons: [],
  });

  const [formData, setFormData] = useState({
    fatherEmail: "",
    fatherName: "",
    fatherPhone: "",
    fatherAadhar: "",
    fatherOccupation: "",
    motherEmail: "",
    motherName: "",
    motherPhone: "",
    motherAadhar: "",
    motherOccupation: "",
    street1: "",
    street2: "",
    city: "",
    stateId: "",
    pin: "",
  });

  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "preferredCommunication") {
      setFormData({ ...formData, preferredCommunication: parseInt(value) });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleDialogClose = () => {
    setDialog({ ...dialog, visible: false });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (
      !(
        formData.fatherName ||
        formData.fatherEmail ||
        formData.fatherPhone ||
        formData.fatherAadhar ||
        formData.fatherOccupation ||
        formData.motherName ||
        formData.motherEmail ||
        formData.motherPhone ||
        formData.motherAadhar ||
        formData.motherOccupation
      )
    ) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Please provide either father details or mother details.",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
      return;
    }

    if (
      (formData.fatherAadhar && !isValidAadhar(formData.fatherAadhar)) ||
      (formData.motherAadhar && !isValidAadhar(formData.motherAadhar))
    ) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Please enter a valid aadhar number",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
      return;
    }
    if (
      (formData.fatherPhone && !isValidPhoneNumber(formData.fatherPhone)) ||
      (formData.motherPhone && !isValidPhoneNumber(formData.motherPhone))
    ) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Please enter a valid 10-digit phone number",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
      return;
    }
    if (!isValidPin(formData.pin)) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Please enter a valid 6-digit pincode",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
      return;
    }

    try {
      if (isAuthenticated()) {
        const response = await updateStudentInfo({
          ...formData,
          studentId: studentId,
        });
        // console.log("RES", response?.data);
        if (response) {
          if (response?.data?.status === 200) {
            setDialog({
              visible: true,
              title: "Success",
              message: "Information updated",
              buttons: [{ name: "OK", onPress: handleDialogClose }],
            });
            setFormData({
              fatherEmail: "",
              fatherName: "",
              fatherPhone: "",
              fatherAadhar: "",
              fatherOccupation: "",
              motherEmail: "",
              motherName: "",
              motherPhone: "",
              motherAadhar: "",
              motherOccupation: "",
              street1: "",
              street2: "",
              city: "",
              stateId: "",
              pin: "",
            });
            setShowRegisterStudent(false);
          } else {
            const errorMessage =
              response?.data?.errorMessage || "Something went wrong";
             setDialog({
               visible: true,
               title: "Error",
               message: errorMessage,
               buttons: [{ name: "Close", onPress: handleDialogClose }],
             });
          }
        } else {
          setDialog({
            visible: true,
            title: "Error",
            message: "Invalid response from the server",
            buttons: [{ name: "Close", onPress: handleDialogClose }],
          });
        }
      } else {
         setDialog({
           visible: true,
           title: "Unauthorized action",
           message: "Please login and try again",
           buttons: [{ name: "Close", onPress: handleDialogClose }],
         });
      }
    } catch (error) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Something went wrong",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CustomDialog
        visible={dialog.visible}
        title={dialog.title}
        message={dialog.message}
        buttons={dialog.buttons}
        onClose={handleDialogClose}
      />
      <Box sx={{ maxWidth: "75%", margin: "auto", padding: 3 }}>
        <Paper elevation={3} sx={{ padding: 5 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontWeight: 600, fontSize: 22, color: "black" }}
          >
            Update Student Information
          </Typography>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={6} lg={4}>
                <TextField
                  fullWidth
                  label="Father's Email"
                  name="fatherEmail"
                  value={formData.fatherEmail}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={4}>
                <TextField
                  fullWidth
                  label="Father's Name"
                  name="fatherName"
                  value={formData.fatherName}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={4}>
                <TextField
                  fullWidth
                  label="Father's Phone"
                  name="fatherPhone"
                  value={formData.fatherPhone}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <TextField
                  fullWidth
                  label="Father's Aadhar"
                  name="fatherAadhar"
                  value={formData.fatherAadhar}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <TextField
                  fullWidth
                  label="Father's Occupation"
                  name="fatherOccupation"
                  value={formData.fatherOccupation}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={4}>
                <TextField
                  fullWidth
                  label="Mother's Email"
                  name="motherEmail"
                  value={formData.motherEmail}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={4}>
                <TextField
                  fullWidth
                  label="Mother's Name"
                  name="motherName"
                  value={formData.motherName}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={4}>
                <TextField
                  fullWidth
                  label="Mother's Phone"
                  name="motherPhone"
                  value={formData.motherPhone}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <TextField
                  fullWidth
                  label="Mother's Aadhar"
                  name="motherAadhar"
                  value={formData.motherAadhar}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <TextField
                  fullWidth
                  label="Mother's Occupation"
                  name="motherOccupation"
                  value={formData.motherOccupation}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <TextField
                  fullWidth
                  label="Street 1"
                  name="street1"
                  value={formData.street1}
                  onChange={handleChange}
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <TextField
                  fullWidth
                  label="Street 2"
                  name="street2"
                  value={formData.street2}
                  onChange={handleChange}
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <TextField
                  fullWidth
                  label="City"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <TextField
                  fullWidth
                  label="PIN"
                  name="pin"
                  value={formData.pin}
                  onChange={handleChange}
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={12}>
                <FormControl fullWidth required size="small">
                  <InputLabel id="state">State</InputLabel>
                  <Select
                    name="stateId"
                    label="State"
                    labelId="state"
                    value={formData.stateId}
                    onChange={handleChange}
                    size="small"
                    disabled={statesLoading}
                  >
                    {statesLoading ? (
                      <MenuItem value="">Loading...</MenuItem>
                    ) : statesError ? (
                      <MenuItem value="">Error fetching states</MenuItem>
                    ) : (
                      states.map((state) => (
                        <MenuItem key={state?.id} value={state?.id}>
                          {state?.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">
                    Preferred Communication
                  </FormLabel>
                  <RadioGroup
                    aria-label="preferredCommunication"
                    name="preferredCommunication"
                    value={formData.preferredCommunication || ""}
                    onChange={handleChange}
                  >
                    <FormControlLabel
                      value="1"
                      control={<Radio disabled={!formData.fatherPhone} />}
                      label="Father"
                    />
                    <FormControlLabel
                      value="2"
                      control={<Radio disabled={!formData.motherPhone} />}
                      label="Mother"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  color="primary"
                  endIcon={<ArrowForward />}
                  disabled={loading}
                  sx={{ textTransform: "unset" }}
                >
                  {loading ? "Loading.." : "Submit"}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Box>
    </>
  );
}

export default UpdateInfoRedirect;
